import React, { useEffect } from "react";
import {
  Text,
  Box,
  HStack,
  VStack,
  Spacer,
  Checkbox,
  Flex,
} from "@chakra-ui/react";
import { TableComponent, Loader, EmptyComponent } from "@components/index";
import { TABLE_ITEM_LAYOUT_TYPE } from "@root/constants/common";
import { useTranslation } from "react-i18next";
import { updatePage } from "@root/store/slices/features/chat";
import { useDispatch, useSelector } from "react-redux";
import {
  getPage,
  getPageCount,
  getPageSize,
} from "@root/store/selectors/features";
import { AppHeader } from "@components/index";
import { StyledDashboardLayoutChildContainer } from "@root/components/common/style";
import useChatHistory from "@root/hooks/useChatHistory";
import ChatSelectionActions from "@root/components/chat-history/ChatSelectionActions";

export const ChatHistory: React.FC = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const page = useSelector(getPage);
  const pageSize = useSelector(getPageSize);
  const pageCount = useSelector(getPageCount);
  const [selectedChats, setSelectedChats] = React.useState<
    Record<string, boolean>
  >({});
  const defaultpage: any = 1;

  const {
    loading,
    threads,
    onClearChat,
    handleChatClick,
    fetchThreads,
    onSearchChange,
    onKeyDown,
    onDateSelect,
    onClear,
    dates,
  } = useChatHistory();
  
  
  useEffect(() => {
    return () => {
      // resetting page count when leaving screen
      dispatch(updatePage(defaultpage));
    };
  }, [dispatch, defaultpage]);

  const renderChats = () => {
    if (loading) {
      return <Loader />;
    }

    if (!threads || threads?.length === 0) {
      return (
        <EmptyComponent
          message={t("translate.noChatHistory")}
          desc={t("translate.startNewChat")}
        />
      );
    }

    const handlePageChange = (page: any) => {
      dispatch(updatePage(page));
      fetchThreads({});
    };

    const handleSelectionChange = (data: any) => {
      setSelectedChats(data);
    };

    return (
      <VStack overflow="auto" align="stretch" spacing={4} w={"100%"}>
        <TableComponent
          tableData={threads}
          itemType={TABLE_ITEM_LAYOUT_TYPE.CHAT_HISTORY}
          onClick={handleChatClick}
          onRemove={onClearChat}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          parentSelectedChat={selectedChats}
          handlePageChange={handlePageChange}
          onSeletionChange={handleSelectionChange}
          modelOption={{}}
          model={{}}
        />
      </VStack>
    );
  };

  return (
    <StyledDashboardLayoutChildContainer maxH={"110vh"} overflow={"hidden"}>
      <Box
        maxW="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        height="105vh"
        overflow="hidden"
      >
        <VStack
          pl={7}
          overflowY={"scroll"}
          align="stretch"
          spacing={4}
          height="105vh"
          overflow="hidden"
          pr={"16px"}
        >
          <Flex justifyContent={"space-between"} alignItems={"center"}>
            <Text
              fontSize={{ base: "18", md: "20" }}
              fontWeight="500"
              minWidth={"120px"}
              pl={"12px"}
              mt={"10px"}
            >
              {t("chat.title")}
            </Text>
            <Text color={"primary.800"} fontWeight={500}>
              {t("chat.autoDeleted")}
            </Text>
          </Flex>

          <AppHeader
            onSearchChange={onSearchChange}
            onKeyDown={onKeyDown}
            showRightInput
            title={t("chat.myChats", { count: threads?.rows?.length })}
            onDateSelect={onDateSelect}
            onClear={onClear}
            dates={dates}
          />
          <HStack width={{ base: "100%", lg: "860px" }} padding={"0px 16px"}>
            <Spacer />
          </HStack>
          {!loading && threads?.length > 0 && (
            <ChatSelectionActions
              threads={threads}
              selectedChats={selectedChats}
              handleSelectAllChange={(isChecked) => {
                setSelectedChats((prevSelectedChats) => {
                  if (!isChecked) {
                    return {};
                  }

                  const updatedSelectedChats = threads.reduce(
                    (acc: Record<string, boolean>, thread: any) => {
                      thread.chats?.forEach((chat: any) => {
                        acc[chat?.id] = true;
                      });
                      return acc;
                    },
                    {} as Record<string, boolean>
                  );

                  return updatedSelectedChats;
                });
              }}
              handleCancelSelection={() => setSelectedChats({})}
              handleRemoveAllChats={() => {
                const selectedChatIds = Object.keys(selectedChats).filter(
                  (key) => selectedChats[key]
                );
                onClearChat({ allChats: selectedChatIds });
                setSelectedChats({});
              }}
            />
          )}
          {renderChats()}
        </VStack>
      </Box>
    </StyledDashboardLayoutChildContainer>
  );
};
